/* 2026-09-12 / kaho + Codex. Fetch native filter links in their own Collection
 * context. Categories settle independently; failure preserves existing links. */
(() => {
  function requestUrl(path, base) {
    const url = new URL(path, base);
    if (url.origin !== new URL(base).origin || !/\/collections\/[^/]+$/.test(url.pathname) || url.search || url.hash) throw new Error('invalid_collection');
    url.searchParams.set('section_id', 'fdp-home-search-source');
    const preview = new URL(base).searchParams.get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
    return url;
  }

  function validateLink(href, path, part, base) {
    const url = new URL(href, base), expected = new URL(path, base);
    if (url.origin !== expected.origin || url.pathname !== expected.pathname) throw new Error('wrong_destination');
    const params = [...url.searchParams];
    if (part.endsWith('-gauge')) {
      if (url.hash !== '#fdp-finder-gauge' || params.length !== 1 || !params[0][0].startsWith('filter.') || !params[0][1]) throw new Error('missing_condition');
    } else if (params.length || !['#fdp-finder-item', '#fdp-finder-placement'].includes(url.hash)) throw new Error('wrong_axis');
    return url.pathname + url.search + url.hash;
  }

  function elementClass(env) {
    return class extends env.HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;
        this.controllers = [];
        const categories = ['standard', 'large'].filter(category => this.dataset[`${category}Url`]);
        Promise.allSettled(categories.map(category => this.loadCategory(category))).then(results => {
          if (!this.isConnected) return;
          const passed = results.filter(result => result.status === 'fulfilled').length;
          this.dataset.fdpState = !categories.length ? 'unconnected' : passed === categories.length ? 'ready' : passed ? 'partial' : 'error';
        });
      }

      disconnectedCallback() {
        this.controllers?.forEach(controller => controller.abort());
      }

      async loadCategory(category) {
        const path = this.dataset[`${category}Url`], base = env.location.href;
        const controller = new env.AbortController();
        this.controllers.push(controller);
        const timeout = env.setTimeout(() => controller.abort(), 10000);
        try {
          const response = await env.fetch(requestUrl(path, base), {signal: controller.signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('http_error');
          const doc = new env.DOMParser().parseFromString(await response.text(), 'text/html');
          const sources = doc.querySelectorAll('[data-fdp-home-search-source]');
          if (sources.length !== 1 || sources[0].dataset.fdpHomeSearchSource !== category || sources[0].dataset.collectionUrl !== path) throw new Error('wrong_source');
          const parts = category === 'standard' ? ['standard-gauge', 'standard-axis'] : ['large-gauge'];
          const replacements = parts.map(part => {
            const source = sources[0].querySelectorAll(`[data-fdp-search-part="${part}"]`);
            const target = this.querySelector(`[data-fdp-search-slot="${part}"]`);
            if (source.length !== 1 || !target) throw new Error('missing_part');
            const links = [...source[0].children].map(link => {
              if (link.tagName !== 'A') throw new Error('invalid_link');
              const href = validateLink(link.getAttribute('href'), path, part, base);
              // Copy only navigation text and allowed formatting; no response scripts/events.
              const copy = env.document.createElement('a');
              copy.setAttribute('href', href);
              if (part.endsWith('-gauge')) copy.className = category === 'standard' ? 'fdp-mobile-gauge fdp-mobile-gauge--wide' : 'fdp-mobile-gauge';
              for (const child of link.children) {
                if (!['STRONG', 'SPAN', 'I'].includes(child.tagName)) throw new Error('invalid_format');
                const text = env.document.createElement(child.tagName.toLowerCase());
                text.textContent = child.textContent;
                if (child.tagName === 'I') text.setAttribute('aria-hidden', 'true');
                copy.append(text);
              }
              if (!copy.textContent.trim()) throw new Error('empty_link');
              return copy;
            });
            const hrefs = links.map(link => link.getAttribute('href'));
            if (new Set(hrefs).size !== hrefs.length) throw new Error('duplicate_links');
            return {target, links};
          });
          if (replacements.every(part => part.links.length === 0)) throw new Error('no_choices');
          if (this.isConnected) for (const {target, links} of replacements) target.replaceChildren(...links);
        } finally {
          env.clearTimeout(timeout);
        }
      }
    };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {requestUrl, validateLink, elementClass};
  if (typeof window !== 'undefined' && !window.customElements.get('fdp-home-search')) window.customElements.define('fdp-home-search', elementClass(window));
})();
